import { useState } from 'react';
import {
    BackButton,
    BackButtonIconType,
    H5Regular,
    SafeAreaView,
    Spinner,
    YStack,
} from 'smoothie';

import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import { SortDirection } from '../../common/types/Common';
import { useViewEvents } from '../../events/hooks/useViewEvents';
import useSearchGuidanceSessions from '../hooks/useSearchGuidanceSessions';
import {
    GuidanceSessionSortField,
    ScheduledEventStartState,
} from '../types/GuidanceSessions';
import {
    CompletedSessionSheet,
    IneligibleSessionSheet,
} from './SelectSessionSheet';
import { SessionCarousel } from './SessionCarousel';

const MAX_WIDTH = 1100;

const SelectSession = () => {
    const { trackViewOnce } = useViewEvents();

    const user = useAuthedUser();
    const [isCompletedSessionSheetOpen, setIsCompletedSessionSheetOpen] =
        useState(false);
    const [isIneligibleSessionSheetOpen, setIsIneligibleSessionSheetOpen] =
        useState(false);

    const { id: userId, membership } = user ?? {};
    const { id: membershipId } = membership ?? {};

    const { data: upcomingSessions, isLoading: isLoadingUpcomingSessions } =
        useSearchGuidanceSessions(
            {
                userId: userId ?? '',
                membershipId: membershipId ?? '',
                state: ScheduledEventStartState.UPCOMING,
                sortField: GuidanceSessionSortField.START_TIME,
                sortDirection: SortDirection.ASC,
            },
            { enabled: membershipId != null && userId != null },
        );

    const { data: pastSessions, isLoading: isLoadingPastSessions } =
        useSearchGuidanceSessions(
            {
                userId: userId ?? '',
                membershipId: membershipId ?? '',
                state: ScheduledEventStartState.PAST,
                sortField: GuidanceSessionSortField.START_TIME,
                sortDirection: SortDirection.DESC,
            },
            { enabled: membershipId != null && userId != null },
        );

    trackViewOnce('GuidanceSessions/SelectSession');

    return (
        <SafeAreaView style={{ flex: 1 }}>
            <YStack
                flex={1}
                width="100%"
                maxWidth={MAX_WIDTH}
                alignSelf="center"
            >
                <YStack padding={16} paddingBottom={0}>
                    <BackButton
                        fallbackRoute="/home/my-guide"
                        iconType={BackButtonIconType.ARROW_LEFT}
                    />
                    <H5Regular paddingLeft={8} marginTop={16} marginBottom={30}>
                        Book your next session
                    </H5Regular>
                </YStack>
                <YStack alignItems="center">
                    {isLoadingPastSessions || isLoadingUpcomingSessions ? (
                        <Spinner marginTop={150} />
                    ) : (
                        <SessionCarousel
                            maxWidth={MAX_WIDTH}
                            baseCardWidth={360}
                            pastSessions={pastSessions?.results ?? []}
                            upcomingSessions={upcomingSessions?.results ?? []}
                            onCompletedCardPress={() => {
                                setIsCompletedSessionSheetOpen(true);
                            }}
                            onIneligibleCardPress={() => {
                                setIsIneligibleSessionSheetOpen(true);
                            }}
                        />
                    )}
                </YStack>

                <CompletedSessionSheet
                    isOpen={isCompletedSessionSheetOpen}
                    onClose={() => {
                        setIsCompletedSessionSheetOpen(false);
                    }}
                />
                <IneligibleSessionSheet
                    isOpen={isIneligibleSessionSheetOpen}
                    onClose={() => {
                        setIsIneligibleSessionSheetOpen(false);
                    }}
                />
            </YStack>
        </SafeAreaView>
    );
};

export default SelectSession;
